import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  List,
  ListItem,
  Divider,
  CircularProgress,
  useTheme,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useAppBar } from '../../providers/AppBarProvider';
import { useConfig } from '../../providers/ConfigProvider';
import axios from 'axios';
import { handleError } from '../../utils/lib';
import ListItemWAvatarRemove from '../../layouts/ListItemWAvatarRemove';
import { useTranslation } from 'react-i18next';
import VetActionsBottomSheet from '../../layouts/VetActionsBottomSheet';

const ViewVetUsers = () => {
  const theme = useTheme();
  const snackbar = useSnackbar();
  const { updateTitle } = useAppBar();
  const { config, isLoadingConfig } = useConfig();
  const [users, setUsers] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [userToPromote, setUserToPromote] = useState(null);
  const [downgradeDialogOpen, setDowngradeDialogOpen] = useState(false);
  const [userToDowngrade, setUserToDowngrade] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const translateRole = (rol) => {
    if (!rol) return '';
    const k = rol.toLowerCase();
    if (k === 'user') return t('view.roleUser');
    if (k === 'admin') return t('view.roleAdmin');
    if (k === 'owner') return t('view.roleOwner');
    return rol;
  };

  useEffect(() => {
    updateTitle('', t('view.vetUsersPageTitle'));
    fetchUsers();
  }, [t]);

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}users?min`,
        { withCredentials: true }
      );
      setUsers(response.data.data || []);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const getNextRole = (currentRole) => {
    if (currentRole?.toLowerCase() === "user") {
      return "ADMIN";
    }
    if (currentRole?.toLowerCase() === "admin") {
      return "OWNER";
    }
    return currentRole; // Si no es user ni admin, mantener el rol actual
  };

  const getDowngradeRole = (currentRole) => {
    if (currentRole?.toLowerCase() === "owner") {
      return "ADMIN";
    }
    if (currentRole?.toLowerCase() === "admin") {
      return "USER";
    }
    return ""; // Si no es owner ni admin, devolver cadena vacía
  };

  const handleDelete = (item) => {
    // Validación: Solo el owner puede eliminar usuarios
    if (!config?.rol || config.rol.toLowerCase() !== "owner") {
      snackbar(t('view.insufficientPrivileges'));
      return;
    }

    setIdToDelete(item.id);
    setDeleteDialogOpen(true);
  };

  const handlePromote = (user) => {
    // Validación: Usuario actual debe tener privilegios suficientes
    if (config?.rol.toLowerCase() === "user") {
      snackbar(t('view.insufficientPrivileges'));
      return;
    }

    // Validación: Admin no puede promover a otro admin
    if (config?.rol.toLowerCase() === "admin" && 
        user.rol.toLowerCase() === "admin") {
      snackbar(t('view.insufficientPrivileges'));
      return;
    }

    // Validación: Usuario ya tiene el privilegio más alto
    if (user.rol.toLowerCase() === "owner") {
      snackbar(t('view.highestPrivilege'));
      return;
    }

    setUserToPromote(user);
    setPromoteDialogOpen(true);
  };

  const confirmPromote = async () => {
    try {
      setLoading(true);
      const nextRole = getNextRole(userToPromote.rol);
      //console.log('nextRole', nextRole);

      await axios.put(
        `${import.meta.env.VITE_API_URL}users?promote&id=${userToPromote.id}`,
        {
          id: userToPromote.id,
          rol: nextRole
        },
        { withCredentials: true }
      );
      fetchUsers(); // Recargar la lista para actualizar roles
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setPromoteDialogOpen(false);
      setUserToPromote(null);
    }
  };

  const handleDowngrade = (user) => {
    // Validación: Usuario actual debe tener privilegios suficientes
    if (config?.rol.toLowerCase() === "user") {
      snackbar(t('view.insufficientPrivileges'));
      return;
    }

    // Validación: Admin no puede degradar a otro admin
    if (config?.rol.toLowerCase() === "admin" && 
        user.rol.toLowerCase() === "admin") {
      snackbar(t('view.insufficientPrivileges'));
      return;
    }

    // Validación: Usuario ya tiene el privilegio más bajo
    if (user.rol.toLowerCase() === "user") {
      snackbar(t('view.lowestPrivilege'));
      return;
    }

    setUserToDowngrade(user);
    setDowngradeDialogOpen(true);
  };

  const confirmDowngrade = async () => {
    try {
      setLoading(true);
      const downgradeRole = getDowngradeRole(userToDowngrade.rol);
      
      await axios.put(
        `${import.meta.env.VITE_API_URL}users?promote&id=${userToDowngrade.id}`,
        {
          id: userToDowngrade.id,
          rol: downgradeRole
        },
        { withCredentials: true }
      );
      fetchUsers(); // Recargar la lista para actualizar roles
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setDowngradeDialogOpen(false);
      setUserToDowngrade(null);
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.put(
        `${import.meta.env.VITE_API_URL}users?remove_from_vet&id=${idToDelete}`,
        { 
          id: idToDelete,
          withCredentials: true 
        }
      );
      setUsers(users.filter(s => s.id !== idToDelete));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setIdToDelete(null);
    }
  };

  const isCurrentUser = (userId) => {
    //console.log('Comparando userId:', userId, 'con config.id:', config?.id);
    return userId === config?.id;
  };

  if (isLoading || isLoadingConfig) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Paper sx={{ p: 2 }}>
        <List>
          {users.map((user, index) => (
            <React.Fragment key={user.id}>
              <ListItem
                secondaryAction={
                  !isCurrentUser(user.id) && (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title={t('view.promoteUser')}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handlePromote(user)}
                          disabled={loading}
                        >
                          <ArrowUpwardIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('view.demoteUser')}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDowngrade(user)}
                          disabled={loading}
                        >
                          <ArrowDownwardIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('view.deleteUser')}>
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleDelete(user)}
                          color="default"
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
                sx={{ paddingRight: isCurrentUser(user.id) ? "16px" : "224px" }}
              >
                <ListItemWAvatarRemove
                  primary={user.name}
                  secondary={`${user.email} - ${translateRole(user.rol)}`}
                  avatarContent={<PersonIcon />}
                  thumbUrl={user.thumb_url}
                />
              </ListItem>
              {index < users.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('common.confirmDeletion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('view.confirmDeleteUserFromVet')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus disabled={loading}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promote Confirmation Dialog */}
      <Dialog
        open={promoteDialogOpen}
        onClose={() => setPromoteDialogOpen(false)}
        aria-labelledby="promote-dialog-title"
        aria-describedby="promote-dialog-description"
      >
        <DialogTitle id="promote-dialog-title">
          {t('view.confirmPromotion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="promote-dialog-description">
            {t('view.confirmPromoteUserMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPromoteDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmPromote} color="primary" autoFocus disabled={loading}>
            {t('view.promoteUserButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Downgrade Confirmation Dialog */}
      <Dialog
        open={downgradeDialogOpen}
        onClose={() => setDowngradeDialogOpen(false)}
        aria-labelledby="downgrade-dialog-title"
        aria-describedby="downgrade-dialog-description"
      >
        <DialogTitle id="downgrade-dialog-title">
          {t('view.confirmDemotion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="downgrade-dialog-description">
            {t('view.confirmDemoteUserMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDowngradeDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDowngrade} color="warning" autoFocus disabled={loading}>
            {t('view.demoteUserButton')}
          </Button>
        </DialogActions>
      </Dialog>

      <VetActionsBottomSheet />
    </Container>
  );
};

export default ViewVetUsers; 